import { useCallback, useState, type KeyboardEvent } from "react";

// The ONE tab-keyboard hook for every tablist on the site (WAI-ARIA tabs
// pattern): Left/Up and Right/Down move to the previous/next tab (wrapping),
// Home/End jump to the ends, and focus follows the selection. Tabs are found
// as role="tab" siblings, so it works inside PillTabScroller too.
// Import it from this file, not the `@/hooks` barrel (that barrel pulls in
// the axios instance).
export function useRovingTabs(count: number, initialIndex = 0) {
  const initialState = { activeIndex: initialIndex };
  const [state, setState] = useState(initialState);

  const select = useCallback(
    (activeIndex: number) => setState((state) => ({ ...state, activeIndex })),
    [],
  );

  const onTabKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const targets: Record<string, number> = {
      ArrowRight: (index + 1) % count,
      ArrowDown: (index + 1) % count,
      ArrowLeft: (index - 1 + count) % count,
      ArrowUp: (index - 1 + count) % count,
      Home: 0,
      End: count - 1,
    };
    const nextIndex = targets[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    select(nextIndex);
    event.currentTarget.parentElement
      ?.querySelectorAll<HTMLElement>('[role="tab"]')
      [nextIndex]?.focus();
  };

  return { activeIndex: state.activeIndex, select, onTabKeyDown };
}
